/**
 * Global Situation Map v2.0 - Frontend
 * CesiumJS 3D globe with real-time delta WebSocket feed,
 * search/filter, trails, and alert notifications.
 * Uses global Cesium from vite-plugin-cesium.
 */
const Cesium = window.Cesium;
if (!Cesium) {
  console.error('Cesium global not found!');
  document.body.innerHTML = '<div style="color:#f00;font-family:monospace;padding:20px">Cesium failed to load. Check console.</div>';
}


// ============================================================
// CONFIG
// ============================================================
const WS_PROTOCOL = location.protocol === "https:" ? "wss:" : "ws:";
const WS_URL = `${WS_PROTOCOL}//${location.host}/ws`;
const API_URL = `${location.protocol}//${location.host}/api`;

const COLORS = {
  commercial: Cesium.Color.fromCssColorString("#00ff88"),
  private: Cesium.Color.fromCssColorString("#00ccff"),
  military: Cesium.Color.fromCssColorString("#ff3333"),
  unknown: Cesium.Color.fromCssColorString("#ffff00"),
  cargo: Cesium.Color.fromCssColorString("#ff8800"),
  tanker: Cesium.Color.fromCssColorString("#ffaa00"),
  passenger: Cesium.Color.fromCssColorString("#cc66ff"),
  gps: Cesium.Color.fromCssColorString("#00ffcc"),
  starlink: Cesium.Color.fromCssColorString("#ffffff"),
  weather: Cesium.Color.fromCssColorString("#66aaff"),
  comms: Cesium.Color.fromCssColorString("#ffcc00"),
  science: Cesium.Color.fromCssColorString("#88ff88"),
  debris: Cesium.Color.fromCssColorString("#666666"),
  fishing: Cesium.Color.fromCssColorString("#88ccaa"),
  tug: Cesium.Color.fromCssColorString("#aaaaaa"),
  other: Cesium.Color.fromCssColorString("#888888"),
};

const GPS_COLOR = Cesium.Color.fromCssColorString("#ff0000");
const GPS_FILL_COLOR = Cesium.Color.fromCssColorString("#ff0000").withAlpha(0.15);

// ============================================================
// USER PREFERENCES (localStorage)
// ============================================================
const PREFS_KEY = "gsm_prefs";
const DEFAULT_PREFS = {
  homeLocation: { lat: 20, lon: 0, height: 20000000 },
  layers: { aircraft: true, ships: true, satellites: true, gps: true },
  search: "",
  altMin: 0,
  altMax: 60000,
  speedMin: 0,
  speedMax: 1000,
  showTrails: false,
};

let prefs = loadPrefs();

function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? { ...DEFAULT_PREFS, ...JSON.parse(raw) } : { ...DEFAULT_PREFS };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function savePrefs() {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch {}
}

// ============================================================
// VIEWER INIT
// ============================================================
const viewer = new Cesium.Viewer("map", {
  baseLayer: Cesium.ImageryLayer.fromProviderAsync(
    Cesium.TileMapServiceImageryProvider.fromUrl(
      Cesium.buildModuleUrl("Assets/Textures/NaturalEarthII")
    )
  ),
  baseLayerPicker: false,
  geocoder: false,
  homeButton: false,
  sceneModePicker: false,
  navigationHelpButton: false,
  animation: false,
  timeline: false,
  fullscreenButton: false,
  vrButton: false,
  infoBox: false,
  selectionIndicator: false,
  creditContainer: document.createElement("div"),
});

const scene = viewer.scene;
scene.globe.baseColor = Cesium.Color.fromCssColorString("#0a0a1a");
scene.globe.enableLighting = false;
scene.skyAtmosphere.show = false;
scene.fog.enabled = false;
scene.backgroundColor = Cesium.Color.fromCssColorString("#000804");
scene.globe.showGroundAtmosphere = false;
scene.globe.depthTestAgainstTerrain = false;

// Set home camera
viewer.camera.setView({
  destination: Cesium.Cartesian3.fromDegrees(
    prefs.homeLocation.lon,
    prefs.homeLocation.lat,
    prefs.homeLocation.height
  ),
});

// ============================================================
// ENTITY STORES
// ============================================================
const entities = {
  aircraft: new Map(),
  ships: new Map(),
  satellites: new Map(),
};

// Render markers through primitive collections. Entity point graphics are
// convenient, but their ground/height resolution can be re-evaluated during
// camera motion. A PointPrimitive keeps a fixed Cartesian position and lets
// Cesium's scene transform do the only projection.
const markerCollections = {
  aircraft: viewer.scene.primitives.add(new Cesium.PointPrimitiveCollection()),
  ships: viewer.scene.primitives.add(new Cesium.PointPrimitiveCollection()),
  satellites: viewer.scene.primitives.add(new Cesium.PointPrimitiveCollection()),
};
const markerPrimitives = {
  aircraft: new Map(),
  ships: new Map(),
  satellites: new Map(),
};

const trails = new Map(); // entity_id -> polyline entity

// Layer visibility
const layerVisible = { ...prefs.layers };

// ============================================================
// ENTITY FACTORIES
// ============================================================
function colorFor(category) {
  return COLORS[category] || COLORS.unknown;
}

// The map is a ground-track display.  Keep the source altitude in the
// entity's data, but pin the rendered marker to the ellipsoid so it does not
// visually float above the imagery (especially aircraft and satellites).
function groundPosition(item) {
  // Use an explicit ellipsoid position instead of Cesium's terrain clamp.
  // This app has imagery but no terrain provider; CLAMP_TO_GROUND can be
  // re-resolved during camera movement and make points appear to drift.
  return Cesium.Cartesian3.fromDegrees(item.lon, item.lat, 10);
}

function addMarker(type, id, item, color, pixelSize, entity) {
  const marker = markerCollections[type].add({
    id: entity,
    position: groundPosition(item),
    pixelSize,
    color,
    outlineColor: Cesium.Color.WHITE.withAlpha(0.6),
    outlineWidth: 1,
    disableDepthTestDistance: 0,
  });
  markerPrimitives[type].set(id, marker);
  return marker;
}

function updateMarker(type, id, item, color, pixelSize) {
  const marker = markerPrimitives[type].get(id);
  if (!marker) return;
  marker.position = groundPosition(item);
  marker.color = color;
  marker.pixelSize = pixelSize;
}

function removeMarker(type, id) {
  const marker = markerPrimitives[type].get(id);
  if (marker) markerCollections[type].remove(marker);
  markerPrimitives[type].delete(id);
}

function setMarkerVisible(type, id, visible) {
  const marker = markerPrimitives[type].get(id);
  if (marker) marker.show = visible;
}

function makeAircraftEntity(ac) {
  const id = ac.icao || ac.callsign || `ac-${Math.random().toString(36).slice(2)}`;
  const color = colorFor(ac.category || "unknown");

  const entity = viewer.entities.add({
    id: `ac_${id}`,
    position: groundPosition(ac),
    point: undefined,
    label: ac.callsign ? {
      text: ac.callsign,
      font: '10px monospace',
      fillColor: color,
      outlineColor: Cesium.Color.BLACK,
      outlineWidth: 2,
      style: Cesium.LabelStyle.FILL_AND_OUTLINE,
      verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
      pixelOffset: new Cesium.Cartesian2(0, -8),
      disableDepthTestDistance: 0,
      show: false, // show on hover
    } : undefined,
    properties: {
      type: "aircraft",
      data: ac,
    },
  });

  addMarker("aircraft", id, ac, color, ac.category === "military" ? 6 : 4, entity);
  entities.aircraft.set(id, entity);
  return entity;
}

function makeShipEntity(s) {
  const id = s.mmsi || `ship-${Math.random().toString(36).slice(2)}`;
  const color = colorFor(s.category || "cargo");

  const entity = viewer.entities.add({
    id: `ship_${id}`,
    position: groundPosition(s),
    point: undefined,
    label: s.name ? {
      text: s.name.substring(0, 20),
      font: '10px monospace',
      fillColor: color,
      outlineColor: Cesium.Color.BLACK,
      outlineWidth: 2,
      style: Cesium.LabelStyle.FILL_AND_OUTLINE,
      verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
      pixelOffset: new Cesium.Cartesian2(0, -8),
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
      show: false,
    } : undefined,
    properties: {
      type: "ship",
      data: s,
    },
  });

  addMarker("ships", id, s, color, s.category === "military" ? 6 : 4, entity);
  entities.ships.set(id, entity);
  return entity;
}

function makeSatelliteEntity(s) {
  const id = s.norad_id || `sat-${Math.random().toString(36).slice(2)}`;
  const color = colorFor(s.category || "unknown");

  const entity = viewer.entities.add({
    id: `sat_${id}`,
    position: groundPosition(s),
    point: undefined,
    path: {
      resolution: 1,
      material: new Cesium.PolylineDashMaterialProperty({
        color: color.withAlpha(0.3),
      }),
      width: 1.5,
      leadTime: 3600,
      trailTime: 3600,
    },
    label: s.name ? {
      text: s.name.substring(0, 25),
      font: '9px monospace',
      fillColor: color.withAlpha(0.8),
      outlineColor: Cesium.Color.BLACK,
      outlineWidth: 2,
      style: Cesium.LabelStyle.FILL_AND_OUTLINE,
      verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
      pixelOffset: new Cesium.Cartesian2(0, -6),
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
      show: false,
    } : undefined,
    properties: {
      type: "satellite",
      data: s,
    },
  });

  addMarker("satellites", id, s, color.withAlpha(0.4), 5, entity);
  entities.satellites.set(id, entity);
  return entity;
}

// GPS jamming zones are managed differently (circles, not points)
let gpsEntities = [];

function clearGPSEntities() {
  for (const e of gpsEntities) {
    viewer.entities.remove(e);
  }
  gpsEntities = [];
}

function updateGPSEntities(data) {
  clearGPSEntities();
  for (const item of data) {
    const intensity = item.intensity || 0.5;
    const radiusMeters = (50 + intensity * 200) * 1000;
    const entity = viewer.entities.add({
      id: `gps_${item.lat}_${item.lon}`,
      position: Cesium.Cartesian3.fromDegrees(item.lon, item.lat, 0),
      ellipse: {
        semiMajorAxis: radiusMeters,
        semiMinorAxis: radiusMeters,
        height: 0,
        material: GPS_FILL_COLOR.withAlpha(0.05 + intensity * 0.1),
        outline: true,
        outlineColor: GPS_COLOR.withAlpha(0.3 + intensity * 0.3),
        outlineWidth: 1,
      },
      properties: {
        type: "gps_jamming",
        data: item,
      },
    });
    gpsEntities.push(entity);
  }
}

// ============================================================
// UPDATE HANDLERS
// ============================================================
function updateAircraftEntity(ac, existing) {
  const id = ac.icao || ac.callsign || `ac-${Math.random().toString(36).slice(2)}`;
  const color = colorFor(ac.category || "unknown");

  if (existing) {
    existing.position = groundPosition(ac);
    updateMarker("aircraft", id, ac, color, ac.category === "military" ? 6 : 4);
    existing.properties.data = ac;
    if (existing.label && ac.callsign) {
      existing.label.text = ac.callsign;
      existing.label.fillColor = color;
    }
  } else {
    makeAircraftEntity(ac);
  }
}

function updateShipEntity(s, existing) {
  const id = s.mmsi || `ship-${Math.random().toString(36).slice(2)}`;
  const color = colorFor(s.category || "cargo");

  if (existing) {
    existing.position = groundPosition(s);
    updateMarker("ships", id, s, color, s.category === "military" ? 6 : 4);
    existing.properties.data = s;
    if (existing.label && s.name) {
      existing.label.text = s.name.substring(0, 20);
      existing.label.fillColor = color;
    }
  } else {
    makeShipEntity(s);
  }
}

function updateSatelliteEntity(s, existing) {
  const id = s.norad_id || `sat-${Math.random().toString(36).slice(2)}`;
  const color = colorFor(s.category || "unknown");

  if (existing) {
    existing.position = groundPosition(s);
    updateMarker("satellites", id, s, color.withAlpha(0.4), 5);
    existing.properties.data = s;
    if (existing.label && s.name) {
      existing.label.text = s.name.substring(0, 25);
      existing.label.fillColor = color.withAlpha(0.8);
    }
  } else {
    makeSatelliteEntity(s);
  }
}

// ============================================================
// FULL STATE HANDLERS
// ============================================================
function handleFullAircraft(data) {
  const counts = { commercial: 0, private: 0, military: 0, unknown: 0 };
  const newIds = new Set();

  for (const ac of data) {
    const id = ac.icao || ac.callsign || `ac-${Math.random().toString(36).slice(2)}`;
    newIds.add(id);
    counts[ac.category] = (counts[ac.category] || 0) + 1;
    const existing = entities.aircraft.get(id);
    updateAircraftEntity(ac, existing);
  }

  // Remove stale
  for (const [id, entity] of entities.aircraft) {
    if (!newIds.has(id)) {
      viewer.entities.remove(entity);
      removeMarker("aircraft", id);
      entities.aircraft.delete(id);
    }
  }

  document.getElementById("ac-count").textContent = data.length;
  document.getElementById("ac-commercial").textContent = counts.commercial;
  document.getElementById("ac-private").textContent = counts.private;
  document.getElementById("ac-military").textContent = counts.military;
}

function handleFullShips(data) {
  const newIds = new Set();
  for (const s of data) {
    const id = s.mmsi || `ship-${Math.random().toString(36).slice(2)}`;
    newIds.add(id);
    const existing = entities.ships.get(id);
    updateShipEntity(s, existing);
  }
  for (const [id, entity] of entities.ships) {
    if (!newIds.has(id)) {
      viewer.entities.remove(entity);
      removeMarker("ships", id);
      entities.ships.delete(id);
    }
  }
  document.getElementById("ship-count").textContent = data.length;
}

function handleFullSatellites(data) {
  const newIds = new Set();
  for (const s of data) {
    const id = s.norad_id || `sat-${Math.random().toString(36).slice(2)}`;
    newIds.add(id);
    const existing = entities.satellites.get(id);
    updateSatelliteEntity(s, existing);
  }
  for (const [id, entity] of entities.satellites) {
    if (!newIds.has(id)) {
      viewer.entities.remove(entity);
      removeMarker("satellites", id);
      entities.satellites.delete(id);
    }
  }
  document.getElementById("sat-count").textContent = data.length;
}

// ============================================================
// DELTA HANDLERS
// ============================================================
function handleAircraftDelta(delta) {
  const counts = { commercial: 0, private: 0, military: 0, unknown: 0 };

  for (const ac of delta.additions || []) {
    const id = ac.icao || ac.callsign || `ac-${Math.random().toString(36).slice(2)}`;
    counts[ac.category] = (counts[ac.category] || 0) + 1;
    updateAircraftEntity(ac, null);
  }
  for (const ac of delta.updates || []) {
    const id = ac.icao || ac.callsign;
    const existing = entities.aircraft.get(id);
    if (existing) {
      updateAircraftEntity(ac, existing);
    } else {
      updateAircraftEntity(ac, null);
    }
  }
  for (const ac of delta.removals || []) {
    const id = ac.icao || ac.callsign;
    const entity = entities.aircraft.get(id);
    if (entity) {
      viewer.entities.remove(entity);
      removeMarker("aircraft", id);
      entities.aircraft.delete(id);
    }
  }

  // Update counts from total
  const total = entities.aircraft.size;
  document.getElementById("ac-count").textContent = total;
}

function handleShipsDelta(delta) {
  for (const s of delta.additions || []) {
    const id = s.mmsi || `ship-${Math.random().toString(36).slice(2)}`;
    updateShipEntity(s, null);
  }
  for (const s of delta.updates || []) {
    const id = s.mmsi;
    const existing = entities.ships.get(id);
    if (existing) {
      updateShipEntity(s, existing);
    } else {
      updateShipEntity(s, null);
    }
  }
  for (const s of delta.removals || []) {
    const id = s.mmsi;
    const entity = entities.ships.get(id);
    if (entity) {
      viewer.entities.remove(entity);
      removeMarker("ships", id);
      entities.ships.delete(id);
    }
  }
  document.getElementById("ship-count").textContent = entities.ships.size;
}

function handleSatellitesDelta(delta) {
  for (const s of delta.additions || []) {
    const id = s.norad_id || `sat-${Math.random().toString(36).slice(2)}`;
    updateSatelliteEntity(s, null);
  }
  for (const s of delta.updates || []) {
    const id = s.norad_id;
    const existing = entities.satellites.get(id);
    if (existing) {
      updateSatelliteEntity(s, existing);
    } else {
      updateSatelliteEntity(s, null);
    }
  }
  for (const s of delta.removals || []) {
    const id = s.norad_id;
    const entity = entities.satellites.get(id);
    if (entity) {
      viewer.entities.remove(entity);
      removeMarker("satellites", id);
      entities.satellites.delete(id);
    }
  }
  document.getElementById("sat-count").textContent = entities.satellites.size;
}

// ============================================================
// SEARCH / FILTER
// ============================================================
function applyFilters() {
  const search = prefs.search.toLowerCase();
  const altMin = prefs.altMin;
  const altMax = prefs.altMax;
  const speedMin = prefs.speedMin;
  const speedMax = prefs.speedMax;

  // Aircraft filter
  for (const [id, entity] of entities.aircraft) {
    const data = entity.properties?.data;
    if (!data) { entity.show = layerVisible.aircraft; setMarkerVisible("aircraft", id, layerVisible.aircraft); continue; }

    let visible = layerVisible.aircraft;

    if (visible && search) {
      const callsign = (data.callsign || "").toLowerCase();
      const icao = (data.icao || "").toLowerCase();
      const reg = (data.registration || "").toLowerCase();
      visible = callsign.includes(search) || icao.includes(search) || reg.includes(search);
    }

    if (visible) {
      const alt = data.altitude_ft || 0;
      visible = alt >= altMin && alt <= altMax;
    }

    if (visible) {
      const speed = data.speed_kts || 0;
      visible = speed >= speedMin && speed <= speedMax;
    }

    entity.show = visible;
    setMarkerVisible("aircraft", id, visible);
  }

  // Ships filter
  for (const [id, entity] of entities.ships) {
    const data = entity.properties?.data;
    if (!data) { entity.show = layerVisible.ships; setMarkerVisible("ships", id, layerVisible.ships); continue; }

    let visible = layerVisible.ships;

    if (visible && search) {
      const name = (data.name || "").toLowerCase();
      const mmsi = (data.mmsi || "").toLowerCase();
      visible = name.includes(search) || mmsi.includes(search);
    }

    if (visible) {
      const speed = data.speed_kts || 0;
      visible = speed >= speedMin && speed <= speedMax;
    }

    entity.show = visible;
    setMarkerVisible("ships", id, visible);
  }

  // Satellites filter
  for (const [id, entity] of entities.satellites) {
    const data = entity.properties?.data;
    if (!data) { entity.show = layerVisible.satellites; setMarkerVisible("satellites", id, layerVisible.satellites); continue; }

    let visible = layerVisible.satellites;

    if (visible && search) {
      const name = (data.name || "").toLowerCase();
      const norad = (data.norad_id || "").toLowerCase();
      visible = name.includes(search) || norad.includes(search);
    }

    entity.show = visible;
    setMarkerVisible("satellites", id, visible);
  }

  // GPS filter
  for (const entity of gpsEntities) {
    entity.show = layerVisible.gps;
  }
}

// ============================================================
// UI EVENT HANDLERS
// ============================================================
function setupUI() {
  // Layer toggles
  document.getElementById("layer-aircraft").addEventListener("change", e => {
    layerVisible.aircraft = e.target.checked;
    prefs.layers.aircraft = e.target.checked;
    savePrefs();
    applyFilters();
  });
  document.getElementById("layer-ships").addEventListener("change", e => {
    layerVisible.ships = e.target.checked;
    prefs.layers.ships = e.target.checked;
    savePrefs();
    applyFilters();
  });
  document.getElementById("layer-satellites").addEventListener("change", e => {
    layerVisible.satellites = e.target.checked;
    prefs.layers.satellites = e.target.checked;
    savePrefs();
    applyFilters();
  });
  document.getElementById("layer-gps").addEventListener("change", e => {
    layerVisible.gps = e.target.checked;
    prefs.layers.gps = e.target.checked;
    savePrefs();
    applyFilters();
  });

  // Search input
  const searchInput = document.getElementById("search-input");
  searchInput.value = prefs.search;
  searchInput.addEventListener("input", e => {
    prefs.search = e.target.value;
    savePrefs();
    applyFilters();
  });

  // Altitude sliders
  const altMinSlider = document.getElementById("alt-min");
  const altMaxSlider = document.getElementById("alt-max");
  const altMinVal = document.getElementById("alt-min-val");
  const altMaxVal = document.getElementById("alt-max-val");

  altMinSlider.value = prefs.altMin;
  altMaxSlider.value = prefs.altMax;
  altMinVal.textContent = prefs.altMin >= 1000 ? `${Math.round(prefs.altMin / 1000)}k` : prefs.altMin;
  altMaxVal.textContent = prefs.altMax >= 1000 ? `${Math.round(prefs.altMax / 1000)}k` : prefs.altMax;

  altMinSlider.addEventListener("input", e => {
    prefs.altMin = parseInt(e.target.value);
    altMinVal.textContent = prefs.altMin >= 1000 ? `${Math.round(prefs.altMin / 1000)}k` : prefs.altMin;
    savePrefs();
    applyFilters();
  });
  altMaxSlider.addEventListener("input", e => {
    prefs.altMax = parseInt(e.target.value);
    altMaxVal.textContent = prefs.altMax >= 1000 ? `${Math.round(prefs.altMax / 1000)}k` : prefs.altMax;
    savePrefs();
    applyFilters();
  });

  // Speed sliders
  const speedMinSlider = document.getElementById("speed-min");
  const speedMaxSlider = document.getElementById("speed-max");
  const speedMinVal = document.getElementById("speed-min-val");
  const speedMaxVal = document.getElementById("speed-max-val");

  speedMinSlider.value = prefs.speedMin;
  speedMaxSlider.value = prefs.speedMax;
  speedMinVal.textContent = prefs.speedMin >= 1000 ? `${Math.round(prefs.speedMin / 1000)}k` : prefs.speedMin;
  speedMaxVal.textContent = prefs.speedMax >= 1000 ? `${Math.round(prefs.speedMax / 1000)}k` : prefs.speedMax;

  speedMinSlider.addEventListener("input", e => {
    prefs.speedMin = parseInt(e.target.value);
    speedMinVal.textContent = prefs.speedMin >= 1000 ? `${Math.round(prefs.speedMin / 1000)}k` : prefs.speedMin;
    savePrefs();
    applyFilters();
  });
  speedMaxSlider.addEventListener("input", e => {
    prefs.speedMax = parseInt(e.target.value);
    speedMaxVal.textContent = prefs.speedMax >= 1000 ? `${Math.round(prefs.speedMax / 1000)}k` : prefs.speedMax;
    savePrefs();
    applyFilters();
  });

  // Trails toggle
  const trailsToggle = document.getElementById("filter-trails");
  trailsToggle.checked = prefs.showTrails;
  trailsToggle.addEventListener("change", e => {
    prefs.showTrails = e.target.checked;
    savePrefs();
    toggleTrails(e.target.checked);
  });

  // Collapsible sections
  document.querySelectorAll(".collapsible").forEach(el => {
    el.addEventListener("click", () => {
      el.classList.toggle("collapsed");
      const target = document.getElementById(el.dataset.target);
      if (target) target.classList.toggle("hidden");
    });
  });

  // Set initial checkbox states
  document.getElementById("layer-aircraft").checked = prefs.layers.aircraft;
  document.getElementById("layer-ships").checked = prefs.layers.ships;
  document.getElementById("layer-satellites").checked = prefs.layers.satellites;
  document.getElementById("layer-gps").checked = prefs.layers.gps;
}

// ============================================================
// TRAILS
// ============================================================
async function toggleTrails(show) {
  if (!show) {
    for (const [id, entity] of trails) {
      viewer.entities.remove(entity);
    }
    trails.clear();
    return;
  }

  // Fetch trails for visible satellites
  for (const [id, entity] of entities.satellites) {
    const data = entity.properties?.data;
    if (!data?.norad_id) continue;
    try {
      const resp = await fetch(`${API_URL}/history/satellites/${data.norad_id}?since=600`);
      if (!resp.ok) continue;
      const json = await resp.json();
      const positions = json.positions;
      if (!positions || positions.length < 2) continue;

      const cartesians = positions.map(p =>
        Cesium.Cartesian3.fromDegrees(p.lon, p.lat, (p.alt || 400) * 1000)
      );

      const trailEntity = viewer.entities.add({
        id: `trail_${id}`,
        polyline: {
          positions: cartesians,
          width: 1,
          material: new Cesium.PolylineDashMaterialProperty({
            color: colorFor(data.category).withAlpha(0.4),
          }),
        },
      });
      trails.set(id, trailEntity);
    } catch (e) {
      // ignore
    }
  }
}

// ============================================================
// ALERT NOTIFICATIONS
// ============================================================
function showAlert(alert) {
  const container = document.getElementById("alert-toast");
  const el = document.createElement("div");
  el.className = "alert-item";
  el.innerHTML = `<div class="alert-title">${alert.rule_name || "ALERT"}</div><div>${alert.trigger?.aircraft?.callsign || alert.trigger?.ship?.name || alert.trigger?.satellite?.name || ""}</div>`;
  container.appendChild(el);
  setTimeout(() => el.remove(), 8000);
}

// ============================================================
// CLICK HANDLER (entity info)
// ============================================================
viewer.screenSpaceEventHandler.setInputAction((click) => {
  const picked = viewer.scene.pick(click.position);
  if (Cesium.defined(picked) && picked.id && picked.id.properties) {
    const props = picked.id.properties;
    const data = props.data?.getValue?.(new Cesium.JulianDate()) || props.data;
    if (data) {
      const type = props.type?.getValue?.(new Cesium.JulianDate()) || props.type;
      showEntityPopup(data, type);
    }
  }
}, Cesium.ScreenSpaceEventType.LEFT_CLICK);

function showEntityPopup(data, type) {
  const lines = [];
  if (type === "aircraft") {
    lines.push(`<b style="color:#0ff">AIRCRAFT</b>`);
    lines.push(`Callsign: ${data.callsign || "N/A"}`);
    lines.push(`ICAO: ${data.icao || ""}`);
    lines.push(`Category: ${data.category || ""}`);
    lines.push(`Altitude: ${(data.altitude_ft || 0).toLocaleString()} ft`);
    lines.push(`Speed: ${data.speed_kts || 0} kts`);
    lines.push(`Heading: ${data.heading || 0}°`);
    if (data.squawk) lines.push(`Squawk: ${data.squawk}`);
    if (data.registration) lines.push(`Reg: ${data.registration}`);
    if (data.origin) lines.push(`From: ${data.origin}`);
    if (data.destination) lines.push(`To: ${data.destination}`);
  } else if (type === "ship") {
    lines.push(`<b style="color:#0ff">SHIP</b>`);
    lines.push(`Name: ${data.name || "N/A"}`);
    lines.push(`MMSI: ${data.mmsi || ""}`);
    lines.push(`Category: ${data.category || ""}`);
    lines.push(`Speed: ${(data.speed_kts || 0).toFixed(1)} kts`);
    lines.push(`Heading: ${(data.heading || 0).toFixed(1)}°`);
    if (data.status) lines.push(`Status: ${data.status}`);
    if (data.destination) lines.push(`Dest: ${data.destination}`);
  } else if (type === "satellite") {
    lines.push(`<b style="color:#0ff">SATELLITE</b>`);
    lines.push(`Name: ${data.name || "N/A"}`);
    lines.push(`NORAD: ${data.norad_id || ""}`);
    lines.push(`Category: ${data.category || ""}`);
    lines.push(`Orbit: ${data.orbit_type || ""}`);
    lines.push(`Altitude: ${(data.altitude_km || 0).toLocaleString()} km`);
    lines.push(`Velocity: ${data.velocity_km_s || 0} km/s`);
  } else if (type === "gps_jamming") {
    lines.push(`<b style="color:#f44">GPS ANOMALY</b>`);
    lines.push(`Intensity: ${(data.intensity || 0).toFixed(2)}`);
    lines.push(`Source: ${data.source || ""}`);
    lines.push(`Info: ${data.description || ""}`);
  }

  // Simple info popup using Cesium's infoBox replacement
  const popup = document.createElement("div");
  popup.style.cssText = `
    position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
    background: rgba(0, 8, 4, 0.95); border: 1px solid #0f0; color: #0f0;
    padding: 16px 20px; border-radius: 6px; font-family: 'Courier New', monospace;
    font-size: 12px; z-index: 3000; max-width: 300px;
  `;
  popup.innerHTML = lines.join("<br>") + "<br><br><span style='opacity:0.5'>Click anywhere to close</span>";
  document.body.appendChild(popup);
  popup.addEventListener("click", () => popup.remove());
}

// ============================================================
// HOVER (show labels)
// ============================================================
let lastHoveredLabel = null;
viewer.screenSpaceEventHandler.setInputAction((movement) => {
  const picked = viewer.scene.pick(movement.endPosition);
  // Hide previous label
  if (lastHoveredLabel) {
    lastHoveredLabel.label.show = false;
    lastHoveredLabel = null;
  }
  if (Cesium.defined(picked) && picked.id && picked.id.label) {
    picked.id.label.show = true;
    lastHoveredLabel = picked.id;
  }
}, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

// ============================================================
// WEBSOCKET
// ============================================================
let ws;
let reconnectDelay = 1000;

function connectWS() {
  ws = new WebSocket(WS_URL);

  ws.onopen = () => {
    document.getElementById("ws-status").textContent = "CONNECTED";
    document.getElementById("ws-status").classList.remove("blink");
    reconnectDelay = 1000;
    console.log("[WS] connected");
  };

  ws.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data);

      switch (msg.type) {
        case "aircraft":
          if (msg.full) handleFullAircraft(msg.data);
          break;
        case "aircraft_delta":
          handleAircraftDelta(msg.data);
          break;
        case "ships":
          if (msg.full) handleFullShips(msg.data);
          break;
        case "ships_delta":
          handleShipsDelta(msg.data);
          break;
        case "satellites":
          if (msg.full) handleFullSatellites(msg.data);
          break;
        case "satellites_delta":
          handleSatellitesDelta(msg.data);
          break;
        case "gps_jamming":
          updateGPSEntities(msg.data);
          document.getElementById("gps-count").textContent = msg.data.length;
          break;
        case "alert":
          showAlert(msg);
          break;
      }

      document.getElementById("last-update").textContent = new Date().toLocaleTimeString();
    } catch (err) {
      console.error("[WS] message error:", err);
    }
  };

  ws.onclose = () => {
    document.getElementById("ws-status").textContent = "RECONNECTING";
    document.getElementById("ws-status").classList.add("blink");
    console.log(`[WS] closed, reconnecting in ${reconnectDelay}ms`);
    setTimeout(connectWS, reconnectDelay);
    reconnectDelay = Math.min(reconnectDelay * 2, 30000);
  };

  ws.onerror = (err) => console.error("[WS] error:", err);
}

// ============================================================
// INITIAL LOAD
// ============================================================
async function initialLoad() {
  try {
    const [ac, ships, sats, gps] = await Promise.all([
      fetch(`${API_URL}/aircraft`).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/ships`).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/satellites`).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/gps-jamming`).then(r => r.json()).catch(() => []),
    ]);
    if (ac?.length) handleFullAircraft(ac);
    if (ships?.length) handleFullShips(ships);
    if (sats?.length) handleFullSatellites(sats);
    if (gps?.length) {
      updateGPSEntities(gps);
      document.getElementById("gps-count").textContent = gps.length;
    }
    console.log(`[INIT] Loaded: ${ac.length} aircraft, ${ships.length} ships, ${sats.length} sats, ${gps.length} GPS events`);
  } catch (e) {
    console.error("[INIT] error:", e);
  }
}

// ============================================================
// START
// ============================================================
setupUI();
connectWS();
initialLoad();
